/**
 * 批量图片上传组件（MultiImageUpload）
 * 支持拖拽上传、后台并发上传
 * 预览图限制显示前 10 张，超出显示 +N 徽章
 *
 * ==================== 性能优化说明（支持 1 万张图片上传） ====================
 *
 * 1. 细粒度状态订阅
 *    - 分别订阅 progress、status、fileIds 等，避免无关状态变化触发重渲染
 *    - 使用 useShallow 避免对象引用变化导致的不必要渲染
 *
 * 2. 缓存过滤结果
 *    - 使用 useMemo 缓存 pendingFiles 过滤结果
 *    - 避免每次渲染都遍历 10,000 个文件
 *
 * 3. 延迟创建预览 URL
 *    - 只为需要显示的文件（前 10 张）创建 blob URL
 *    - 通过 Store 的 getPreviewUrl 方法延迟创建
 *
 * ==========================================================================
 */

import type { ReactElement, DragEvent, ChangeEvent } from 'react'
import { useState, useRef, useCallback, useMemo, useEffect } from 'react'
import { motion, AnimatePresence } from 'framer-motion'
import { Upload, AlertTriangle, Image as ImageIcon, Loader2, X, FolderOpen, Library } from 'lucide-react'
import { Button } from '@/components/ui/button'
import { Progress } from '@/components/ui/progress'
import { cn } from '@/lib/utils'
import { UPLOAD_CONFIG } from '@/services/uploadService'
import { useUploadStore } from '@/stores/upload'
import type { UploadFileItemWithPreview } from '@/stores/upload'
import { ImagePreviewGrid } from '@/components/workflow/ImagePreviewGrid'
import { MaterialLibraryDialog } from '@/components/workflow/MaterialLibraryDialog'
import type { FormFieldProps } from './types'

/**
 * 格式化文件大小
 * @param bytes 字节数
 * @returns 格式化后的字符串
 */
function formatFileSize(bytes: number): string {
  if (bytes < 1024) {
    return `${String(bytes)} B`
  }
  if (bytes < 1024 * 1024) {
    return `${(bytes / 1024).toFixed(1)} KB`
  }
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`
}

/**
 * 批量图片上传组件
 * @param props 组件属性
 * @returns 组件元素
 */
export function MultiImageUploadField({
  component,
  value,
  onChange,
  error,
  disabled = false,
  userCredits,
  basePower,
}: FormFieldProps): ReactElement {
  const [isDragOver, setIsDragOver] = useState(false)
  const [uploadMode, setUploadMode] = useState<'files' | 'folder'>('files')
  const [showMaterialLibrary, setShowMaterialLibrary] = useState(false)
  const inputRef = useRef<HTMLInputElement>(null)
  const folderInputRef = useRef<HTMLInputElement>(null)

  // Store actions（不订阅状态变化）
  const createSession = useUploadStore((state) => state.createSession)
  const addFilesToSession = useUploadStore((state) => state.addFilesToSession)
  const startUpload = useUploadStore((state) => state.startUpload)
  const removeFile = useUploadStore((state) => state.removeFile)
  const clearSession = useUploadStore((state) => state.clearSession)
  const getSession = useUploadStore((state) => state.getSession)
  const getPreviewUrl = useUploadStore((state) => state.getPreviewUrl)

  // 使用 state 存储 sessionId，确保变化时触发重渲染
  const [sessionId, setSessionId] = useState<string | null>(() => createSession(component.title))
  const latestSessionIdRef = useRef<string | null>(sessionId)
  const previousUploadedUrlsCountRef = useRef(0)

  // 细粒度订阅：分别订阅需要的状态，避免整个 session 变化触发重渲染
  const sessionStatus = useUploadStore((state) =>
    sessionId !== null ? state.sessions[sessionId]?.status : undefined
  )
  const sessionProgress = useUploadStore((state) =>
    sessionId !== null ? state.sessions[sessionId]?.progress : undefined
  )
  const sessionFileIds = useUploadStore((state) =>
    sessionId !== null ? state.sessions[sessionId]?.fileIds : undefined
  )
  const sessionFilesById = useUploadStore((state) =>
    sessionId !== null ? state.sessions[sessionId]?.filesById : undefined
  )
  const sessionUploadedUrls = useUploadStore((state) =>
    sessionId !== null ? state.sessions[sessionId]?.uploadedUrls : undefined
  )

  // 固定最大上传数量（统一 1 万张）
  const maxCount = UPLOAD_CONFIG.MAX_FILES

  // 根据用户算力计算可上传的最大张数（共享算力场景）
  const creditBasedMaxCount = useMemo(() => {
    if (userCredits === undefined || basePower === undefined || basePower <= 0) {
      return Infinity
    }
    return Math.floor(userCredits / basePower)
  }, [userCredits, basePower])

  // 共享算力时受算力限制，专享算力（userCredits 为 undefined）仅受 1 万张上限限制
  const effectiveMaxCount = Math.min(maxCount, creditBasedMaxCount)
  const isLimitedByCredits = effectiveMaxCount < maxCount

  // 当前已上传的 URL 列表（从表单值中获取）
  const uploadedUrls = useMemo(() => {
    if (Array.isArray(value)) {
      // 过滤掉空字符串
      return (value as string[]).filter((url) => url.trim() !== '')
    }
    return []
  }, [value])

  const setActiveSessionId = useCallback((nextSessionId: string): void => {
    latestSessionIdRef.current = nextSessionId
    setSessionId(nextSessionId)
  }, [])

  // 组件卸载时清理会话
  useEffect(() => {
    return (): void => {
      const activeSessionId = latestSessionIdRef.current
      if (activeSessionId !== null) {
        clearSession(activeSessionId)
      }
    }
  }, [clearSession])

  // 外部清空值（如提交成功后 reset）时，重建会话，避免旧 sessionUploadedUrls 回填
  useEffect(() => {
    const previousUploadedUrlsCount = previousUploadedUrlsCountRef.current
    previousUploadedUrlsCountRef.current = uploadedUrls.length

    if (sessionId === null || sessionStatus === 'uploading') {
      return
    }
    if (previousUploadedUrlsCount === 0 || uploadedUrls.length > 0) {
      return
    }
    if (sessionUploadedUrls === undefined || sessionUploadedUrls.length === 0) {
      return
    }

    clearSession(sessionId)
    const newSessionId = createSession(component.title)
    latestSessionIdRef.current = newSessionId
    const timerId = window.setTimeout(() => {
      setActiveSessionId(newSessionId)
    }, 0)
    return (): void => {
      window.clearTimeout(timerId)
    }
  }, [
    sessionId,
    sessionStatus,
    uploadedUrls.length,
    sessionUploadedUrls,
    clearSession,
    createSession,
    component.title,
    setActiveSessionId,
  ])

  // 同步已上传 URL 到表单
  useEffect(() => {
    if (sessionUploadedUrls !== undefined && sessionUploadedUrls.length > 0) {
      // 外部已清空（例如 resetForm）时，不再把旧会话 URL 回填
      if (uploadedUrls.length === 0 && sessionStatus !== 'uploading') {
        return
      }

      // 合并已有的 URL 和新上传的 URL
      const allUrls = [...new Set([...uploadedUrls, ...sessionUploadedUrls])]
      if (allUrls.length !== uploadedUrls.length) {
        onChange(allUrls)
      }
    }
  }, [sessionUploadedUrls, uploadedUrls, sessionStatus, onChange])

  // 缓存：计算待上传的文件（未成功的）
  const pendingFiles = useMemo((): UploadFileItemWithPreview[] => {
    if (sessionFileIds === undefined || sessionFilesById === undefined || sessionId === null) {
      return []
    }

    // 只处理非成功状态的文件，并限制只为前 10 个创建预览 URL
    const nonSuccessIds = sessionFileIds.filter(
      (id) => sessionFilesById[id]?.status !== 'success'
    )

    // 只为需要显示的文件（前 10 个）创建预览 URL
    return nonSuccessIds.slice(0, 10).map((id) => {
      const file = sessionFilesById[id]
      if (file === undefined) {
        return null
      }
      return {
        ...file,
        previewUrl: getPreviewUrl(sessionId, id),
      }
    }).filter((f): f is UploadFileItemWithPreview => f !== null)
  }, [sessionFileIds, sessionFilesById, sessionId, getPreviewUrl])

  // 待上传文件总数（用于显示，不需要创建预览 URL）
  const pendingCount = useMemo(() => {
    if (sessionFileIds === undefined || sessionFilesById === undefined) {
      return 0
    }
    return sessionFileIds.filter((id) => sessionFilesById[id]?.status !== 'success').length
  }, [sessionFileIds, sessionFilesById])

  // 总大小
  const totalSize = useMemo(() => {
    if (sessionFileIds === undefined || sessionFilesById === undefined) {
      return 0
    }
    return sessionFileIds.reduce((acc, id) => {
      const file = sessionFilesById[id]
      return acc + (file?.file.size ?? 0)
    }, 0)
  }, [sessionFileIds, sessionFilesById])

  // 计算状态
  const isUploading = sessionStatus === 'uploading'
  const totalCount = uploadedUrls.length
  const uploadProgress = sessionProgress ?? { uploaded: 0, total: 0, percent: 0, failed: 0 }

  /**
   * 处理文件选择
   */
  const handleFileSelect = useCallback(
    (selectedFiles: FileList | null): void => {
      console.log('[MultiImageUpload] 文件选择触发, 文件数量:', selectedFiles?.length ?? 0)

      if (selectedFiles === null || selectedFiles.length === 0 || disabled) {
        console.log('[MultiImageUpload] 无文件或已禁用, disabled:', disabled)
        return
      }

      let activeSessionId = sessionId
      if (activeSessionId === null || getSession(activeSessionId) === undefined) {
        activeSessionId = createSession(component.title)
        setActiveSessionId(activeSessionId)
        console.warn('[MultiImageUpload] 会话缺失，已重建:', activeSessionId)
      }

      console.log('[MultiImageUpload] 当前会话ID:', activeSessionId)

      // 检查数量限制
      const currentCount = uploadedUrls.length + pendingCount
      const remainingSlots = effectiveMaxCount - currentCount
      if (remainingSlots <= 0) {
        if (isLimitedByCredits) {
          alert(`当前算力最多可上传 ${String(effectiveMaxCount)} 张图片，请充值获取更多算力`)
        } else {
          alert(`最多只能上传 ${String(maxCount)} 张图片`)
        }
        return
      }

      // 验证并过滤文件
      const validFiles: File[] = []
      const errors: string[] = []

      Array.from(selectedFiles)
        .slice(0, remainingSlots)
        .forEach((file) => {
          if (!file.type.startsWith('image/')) {
            // 跳过非图片文件,不报错
            return
          }
          if (file.size > UPLOAD_CONFIG.MAX_FILE_SIZE) {
            errors.push(`${file.name} 超过 8MB 限制`)
            return
          }
          validFiles.push(file)
        })

      if (errors.length > 0) {
        alert(errors.join('\n'))
      }

      if (validFiles.length === 0) {
        console.warn('[MultiImageUpload] 没有有效的文件')
        return
      }

      console.log('[MultiImageUpload] 有效文件数量:', validFiles.length)

      // 添加文件到会话（立即返回，不阻塞）
      addFilesToSession(activeSessionId, validFiles)
      console.log('[MultiImageUpload] 文件已添加到会话')

      // 启动后台上传（不等待）
      console.log('[MultiImageUpload] 启动上传...')
      void startUpload(activeSessionId, (urls) => {
        console.log('[MultiImageUpload] 上传完成, URLs:', urls)
        // 上传完成后更新表单值
        const allUrls = [...new Set([...uploadedUrls, ...urls])]
        onChange(allUrls)
      })
    },
    [
      disabled,
      uploadedUrls,
      pendingCount,
      maxCount,
      effectiveMaxCount,
      isLimitedByCredits,
      addFilesToSession,
      startUpload,
      onChange,
      sessionId,
      getSession,
      createSession,
      component.title,
      setActiveSessionId,
    ]
  )

  /**
   * 处理输入框变化
   */
  function handleInputChange(event: ChangeEvent<HTMLInputElement>): void {
    handleFileSelect(event.target.files)
    event.target.value = ''
  }

  /**
   * 处理拖拽
   */
  function handleDragOver(event: DragEvent<HTMLDivElement>): void {
    event.preventDefault()
    event.stopPropagation()
    if (!disabled && !isUploading) {
      setIsDragOver(true)
    }
  }

  function handleDragLeave(event: DragEvent<HTMLDivElement>): void {
    event.preventDefault()
    event.stopPropagation()
    setIsDragOver(false)
  }

  function handleDrop(event: DragEvent<HTMLDivElement>): void {
    event.preventDefault()
    event.stopPropagation()
    setIsDragOver(false)
    if (!disabled) {
      handleFileSelect(event.dataTransfer.files)
    }
  }

  /**
   * 点击上传区域
   */
  function handleClick(): void {
    if (!disabled) {
      if (uploadMode === 'folder') {
        folderInputRef.current?.click()
      } else {
        inputRef.current?.click()
      }
    }
  }

  /**
   * 移除单个文件
   */
  function handleRemoveFile(fileId: string): void {
    if (sessionId === null || sessionFilesById === undefined) {
      return
    }

    const fileToRemove = sessionFilesById[fileId]
    if (fileToRemove?.url !== undefined) {
      // 如果文件已上传，从表单值中移除
      const newUrls = uploadedUrls.filter((u) => u !== fileToRemove.url)
      onChange(newUrls)
    }

    removeFile(sessionId, fileId)
  }

  /**
   * 移除已上传的 URL
   * @param url 要移除的 URL
   */
  function handleRemoveUrl(url: string): void {
    if (sessionId !== null && sessionFileIds !== undefined && sessionFilesById !== undefined) {
      const matchedFileId = sessionFileIds.find((id) => sessionFilesById[id]?.url === url)
      if (matchedFileId !== undefined) {
        removeFile(sessionId, matchedFileId)
      }
    }

    const newUrls = uploadedUrls.filter((u) => u !== url)
    onChange(newUrls)
  }

  /**
   * 处理从素材库选择的图片
   * @param imageUrls 选中的图片 URL 列表
   */
  const handleMaterialLibraryConfirm = useCallback(
    (imageUrls: string[]): void => {
      if (imageUrls.length === 0) return

      // 计算还能添加多少张（口径与本地上传保持一致：已上传 + 待上传）
      const currentCount = uploadedUrls.length + pendingCount
      const remainingSlots = Math.max(0, effectiveMaxCount - currentCount)
      const urlsToAdd = imageUrls.slice(0, remainingSlots)

      if (urlsToAdd.length > 0) {
        // 合并已有的 URL 和新选择的 URL
        const allUrls = [...new Set([...uploadedUrls, ...urlsToAdd])]
        onChange(allUrls)
      }
    },
    [effectiveMaxCount, uploadedUrls, pendingCount, onChange]
  )

  return (
    <div className="space-y-3">
      <label className="text-sm font-medium">
        {component.title}
        {component.is_required && (
          <span className="ml-1 text-destructive">*</span>
        )}
      </label>

      {/* 上传区域 */}
      <div
        role="button"
        tabIndex={disabled ? -1 : 0}
        onClick={handleClick}
        onKeyDown={(e): void => {
          if (e.key === 'Enter' || e.key === ' ') {
            handleClick()
          }
        }}
        onDragOver={handleDragOver}
        onDragLeave={handleDragLeave}
        onDrop={handleDrop}
        className={cn(
          'flex min-h-[140px] cursor-pointer flex-col items-center justify-center gap-3 rounded-xl border-2 border-dashed p-6 transition-all',
          'bg-muted/30 hover:border-primary hover:bg-primary/5',
          isDragOver && 'border-primary bg-primary/10 scale-[1.01]',
          disabled && 'cursor-not-allowed opacity-50',
          error !== undefined && 'border-destructive'
        )}
      >
        <div className="flex h-12 w-12 items-center justify-center rounded-full bg-primary/10">
          <Upload className="h-6 w-6 text-primary" />
        </div>
        <div className="text-center">
          <p className="text-sm font-medium text-foreground">
            拖拽图片到此处上传
          </p>
          <p className="mt-1.5 text-xs text-muted-foreground">
            或点击下方按钮选择图片/文件夹
          </p>
          <p className="mt-0.5 text-xs text-muted-foreground">
            支持 JPG、PNG、WebP、GIF，单张最大 8MB，最多 <span className="font-medium">{String(maxCount)}</span> 张
          </p>

        </div>

        {/* 上传按钮 - 点击直接触发文件选择 */}
        <div className="mt-2 flex flex-wrap gap-2">
          <Button
            type="button"
            variant="outline"
            size="sm"
            className="h-8 px-4 text-sm bg-background hover:bg-primary hover:text-primary-foreground"
            onClick={(e): void => {
              e.stopPropagation()
              setUploadMode('files')
              inputRef.current?.click()
            }}
            disabled={disabled}
          >
            <Upload className="mr-1.5 h-4 w-4" />
            选择图片
          </Button>
          <Button
            type="button"
            variant="outline"
            size="sm"
            className="h-8 px-4 text-sm bg-background hover:bg-primary hover:text-primary-foreground"
            onClick={(e): void => {
              e.stopPropagation()
              setUploadMode('folder')
              folderInputRef.current?.click()
            }}
            disabled={disabled}
          >
            <FolderOpen className="mr-1.5 h-4 w-4" />
            选择文件夹
          </Button>
          <Button
            type="button"
            variant="outline"
            size="sm"
            className="h-8 px-4 text-sm bg-background hover:bg-primary hover:text-primary-foreground"
            onClick={(e): void => {
              e.stopPropagation()
              setShowMaterialLibrary(true)
            }}
            disabled={disabled}
          >
            <Library className="mr-1.5 h-4 w-4" />
            从素材库选择
          </Button>
        </div>
      </div>

      {/* 图片文件选择 input */}
      <input
        ref={inputRef}
        type="file"
        accept="image/jpeg,image/png,image/webp,image/gif"
        multiple={true}
        onChange={handleInputChange}
        className="hidden"
        aria-label="选择多张图片上传"
      />

      {/* 文件夹选择 input */}
      <input
        ref={folderInputRef}
        type="file"
        accept="image/jpeg,image/png,image/webp,image/gif"
        multiple={true}
        {...({ webkitdirectory: '', directory: '' } as React.InputHTMLAttributes<HTMLInputElement>)}
        onChange={handleInputChange}
        className="hidden"
        aria-label="选择文件夹批量上传"
      />

      {/* 文件统计 */}
      {(totalCount > 0 || pendingCount > 0) && (
        <div className="flex items-center justify-between text-sm">
          <div className="flex items-center gap-4">
            <span className="flex items-center gap-1">
              <ImageIcon className="h-4 w-4 text-muted-foreground" />
              <span className="font-medium">{totalCount + pendingCount}</span>
              <span className="text-muted-foreground">张图片</span>
              {isUploading && (
                <span className="text-primary">
                  （上传中...）
                </span>
              )}
            </span>
            {totalSize > 0 && (
              <span className="text-muted-foreground">
                共 {formatFileSize(totalSize)}
              </span>
            )}
          </div>
        </div>
      )}

      {/* 上传进度（内嵌） */}
      <AnimatePresence>
        {isUploading && (
          <motion.div
            initial={{ opacity: 0, height: 0 }}
            animate={{ opacity: 1, height: 'auto' }}
            exit={{ opacity: 0, height: 0 }}
            className="space-y-2 overflow-hidden"
          >
            <div className="flex items-center gap-2 rounded-lg bg-amber-500/10 p-2 text-amber-600 dark:text-amber-400">
              <AlertTriangle className="h-4 w-4 flex-shrink-0" />
              <span className="text-xs font-medium">正在上传，请勿关闭页面</span>
            </div>
            <div className="space-y-1">
              <div className="flex items-center justify-between text-xs">
                <span className="flex items-center gap-1">
                  <Loader2 className="h-3 w-3 animate-spin text-primary" />
                  上传中...
                </span>
                <span className="font-medium">
                  {uploadProgress.uploaded} / {uploadProgress.total} ({uploadProgress.percent}%)
                </span>
              </div>
              <Progress value={uploadProgress.percent} className="h-1.5" />
            </div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* 正在上传中的文件预览（只显示前 10 张） */}
      {pendingFiles.length > 0 && (
        <ImagePreviewGrid
          items={pendingFiles}
          onRemove={handleRemoveFile}
          disabled={disabled || isUploading}
          maxVisible={10}
          totalCount={pendingCount}
        />
      )}

      {/* 已上传图片缩略图预览 */}
      {uploadedUrls.length > 0 && (
        <UploadedImagesGrid
          urls={uploadedUrls}
          onRemove={handleRemoveUrl}
          disabled={disabled || isUploading}
          maxVisible={10}
        />
      )}

      {error !== undefined && (
        <p className="text-xs text-destructive">{error}</p>
      )}

      {/* 素材库选择对话框 */}
      <MaterialLibraryDialog
        open={showMaterialLibrary}
        onOpenChange={setShowMaterialLibrary}
        onConfirm={handleMaterialLibraryConfirm}
        maxCount={Math.max(0, effectiveMaxCount - (uploadedUrls.length + pendingCount))}
        existingUrls={uploadedUrls}
      />
    </div>
  )
}

// ==================== 子组件 ====================

/**
 * 已上传图片网格组件属性
 */
interface UploadedImagesGridProps {
  /** 图片 URL 列表 */
  urls: string[]
  /** 移除回调 */
  onRemove?: (url: string) => void
  /** 是否禁用 */
  disabled?: boolean
  /** 最大显示数量 */
  maxVisible?: number
}

/**
 * 已上传图片网格组件
 * 显示已成功上传的图片缩略图，支持删除
 * @param props 组件属性
 * @returns 组件元素
 */
function UploadedImagesGrid({
  urls,
  onRemove,
  disabled = false,
  maxVisible = 10,
}: UploadedImagesGridProps): ReactElement | null {
  // 缓存过滤和切片结果
  const { validUrls, visibleUrls, hasOverflow, overflowCount } = useMemo(() => {
    const filtered = urls.filter((url) => url.trim() !== '')
    const overflow = filtered.length > maxVisible
    const visible = overflow ? filtered.slice(0, maxVisible - 1) : filtered
    const count = overflow ? filtered.length - (maxVisible - 1) : 0
    return {
      validUrls: filtered,
      visibleUrls: visible,
      hasOverflow: overflow,
      overflowCount: count,
    }
  }, [urls, maxVisible])

  if (validUrls.length === 0) {
    return null
  }

  return (
    <div
      className="flex flex-wrap rounded-lg border bg-muted/20 p-2"
      style={{ gap: 6 }}
    >
      {visibleUrls.map((url, index) => (
        <div
          key={`${url}-${String(index)}`}
          className="group relative overflow-hidden rounded-lg bg-muted shadow-sm"
          style={{ width: 56, height: 56 }}
        >
          <img
            src={url}
            alt=""
            className="h-full w-full object-cover"
            loading="lazy"
          />
          {/* 删除按钮 */}
          {!disabled && onRemove !== undefined && (
            <Button
              type="button"
              variant="destructive"
              size="icon"
              onClick={(e): void => {
                e.stopPropagation()
                onRemove(url)
              }}
              className="absolute right-1 top-1 h-5 w-5 opacity-0 shadow-sm transition-opacity group-hover:opacity-100"
            >
              <X className="h-3 w-3" />
            </Button>
          )}
        </div>
      ))}

      {/* 溢出徽章 */}
      {hasOverflow && (
        <div
          className={cn(
            'flex items-center justify-center rounded-lg',
            'bg-gradient-to-br from-primary/20 to-primary/10',
            'border-2 border-dashed border-primary/30',
            'text-primary font-semibold'
          )}
          style={{ width: 56, height: 56 }}
        >
          <span className="text-base font-bold">+{overflowCount}</span>
        </div>
      )}
    </div>
  )
}
